import React, {useCallback, useEffect, useMemo, useState} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Helmet} from 'react-helmet'
import {extractFromLocale} from '@modifi/plugin-i18n'
import {clientConfig} from '@modifi/client-config'
import {stripTrailingSlash} from '@modifi/router'
import {useCurrentLocale} from '../../features/route-info'

const propTypes = {
  canonicalLinks: PropTypes.objectOf(PropTypes.string.isRequired),
  hrefLang: PropTypes.objectOf(PropTypes.string.isRequired),
  pathname: PropTypes.string,
}
const defaultProps = {
  canonicalLinks: undefined as null | undefined,
  hrefLang: undefined as null | undefined,
  pathname: '',
}

export type SeoHeaderProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const SeoHeader: React.FC<SeoHeaderProps> = ({canonicalLinks, hrefLang, pathname}) => {
  const currentLocale = useCurrentLocale()
  const [language] = currentLocale ? extractFromLocale(currentLocale) : ['en']
  const slug = pathname?.slice(pathname?.lastIndexOf('/') + 1) || ''
  const baseUrlFromConfig = clientConfig.get('service_canonical_url', '') as string

  const [baseUrl, setBaseUrl] = useState(baseUrlFromConfig)

  useEffect(() => {
    if (!baseUrl || baseUrl.length === 0) {
      setBaseUrl(`${document?.location?.protocol}//${document?.location?.host}`)
    }
  }, [baseUrl])

  const canonicalUrl = useMemo(() => {
    if (canonicalLinks && currentLocale && canonicalLinks[currentLocale]) {
      return stripTrailingSlash(`${baseUrl}${canonicalLinks[currentLocale]}`)
    }
    return undefined
  }, [canonicalLinks, currentLocale, baseUrl])

  const withBaseUrlPrefix = useCallback(
    (path: string): string => stripTrailingSlash(`${baseUrl}${path}`),
    [baseUrl]
  )

  return (
    <Helmet>
      <html lang={language} />

      {/* not generating alternate links for post pages by checking presence of :slug in 
          URL since blog post page in a some language might not be present */}
      {hrefLang &&
        !hrefLang?.en?.includes('/post/:slug') &&
        Object.entries(hrefLang).map(([lang, urlPath]) => (
          <link key={lang} rel="alternate" href={withBaseUrlPrefix(urlPath)} hrefLang={lang} />
        ))}

      {canonicalUrl && <link rel="canonical" href={canonicalUrl.replace(':slug', slug)} />}
    </Helmet>
  )
}

SeoHeader.propTypes = propTypes
SeoHeader.defaultProps = defaultProps

export default SeoHeader
